import React, { useEffect } from 'react';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { useLazyQuery } from '@apollo/client';
import { CircularProgress, ThemeProvider } from '@material-ui/core';
import { usePostHog } from 'posthog-js/react';

import { Product, getTheme } from '@/utils/productHelpers';
import { getSupabaseClient } from '@/utils/supabaseClient';
import {
  activateSparkConfirm,
  requestAuthCode,
} from '@/utils/sparkActivationApi';
import {
  REDIRECT_TO_SESSION_KEY,
  SLEEPIO_EMAIL_INTENT_SESSION_KEY,
} from '@/utils/constants';
import { isSafeRedirectUri } from '@/utils/urlHelpers';
import { ME_QUERY, QUERY_DISTINCT_ID_WITH_EMAIL } from '@/apollo/queries';
import { logError } from '@/utils/logError';

/**
 * Extract entitlement status from a Supabase JWT.
 * Returns 'active', 'pending', or undefined if not present.
 */
const getEntitlementStatus = (accessToken: string): string | undefined => {
  try {
    const payload = JSON.parse(atob(accessToken.split('.')[1]));
    return payload.bh_claims?.products?.[0]?.status;
  } catch {
    return undefined;
  }
};

const EmailAuthCallback: React.FC = () => {
  const history = useHistory();
  const { product, lob } = useParams<{ product: Product; lob: string }>();
  const { search } = useLocation();
  const posthog = usePostHog();
  const [fetchMe] = useLazyQuery<{ me: { uid: string } }>(ME_QUERY, {
    fetchPolicy: 'no-cache',
  });
  const [getDistinctId] = useLazyQuery(QUERY_DISTINCT_ID_WITH_EMAIL, {
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    // Login flow: user already active — skip activation, go straight to session handoff.
    const handleLoginFlow = async () => {
      const {
        data: { session: refreshedSession },
        error: refreshError,
      } = await getSupabaseClient().auth.refreshSession();

      if (
        refreshError
        || !refreshedSession?.access_token
        || !refreshedSession?.refresh_token
      ) {
        logError('[EmailAuthCallback]', 'refreshSession failed', refreshError);
        history.replace('/spark/login');
        return;
      }

      const redirectTo = sessionStorage.getItem(REDIRECT_TO_SESSION_KEY);

      // Browser context: no in-app deep link available — route to download page.
      if (!redirectTo || !isSafeRedirectUri(redirectTo)) {
        history.replace(`/activate/${product}/download-app`);
        return;
      }

      let exchangeToken: string;
      try {
        ({ exchangeToken } = await requestAuthCode(
          refreshedSession.access_token,
          refreshedSession.refresh_token,
        ));
      } catch (err) {
        logError('[EmailAuthCallback]', 'requestAuthCode failed', err);
        history.replace('/spark/login');
        return;
      }

      const { data: meData } = await fetchMe({
        context: {
          headers: { Authorization: `Bearer ${refreshedSession.access_token}` },
        },
      });
      const limbixUserUid = meData?.me?.uid;

      const deepLinkUrl = new URL(redirectTo);
      deepLinkUrl.searchParams.set('exchange_token', exchangeToken);
      if (limbixUserUid) {
        deepLinkUrl.searchParams.set('limbix_user_uid', limbixUserUid);
      }

      window.location.href = deepLinkUrl.toString();
    };

    const handleCallback = async () => {
      try {
        // In-app browser context: Spark appends redirectTo to the verify URL's
        // redirect_to param so it arrives here as a search param. Store it so
        // downstream handoff can use it for the deep link back to the app.
        const redirectToParam = new URLSearchParams(search).get('redirectTo');
        if (redirectToParam && isSafeRedirectUri(redirectToParam)) {
          sessionStorage.setItem(REDIRECT_TO_SESSION_KEY, redirectToParam);
        }

        // Wait for Supabase to process the hash fragment (detectSessionInUrl is async)
        await new Promise((resolve) => {
          setTimeout(resolve, 1500);
        });

        const {
          data: { session },
        } = await getSupabaseClient().auth.getSession();

        if (!session) {
          history.replace(`/activate/${product}/access-code`, {
            error: 'email_verification_failed',
          });
          return;
        }

        const supabaseUserId = session.user?.id ?? '';
        const patientUid = session.user?.user_metadata?.limbixUserId || '';
        const entitlementStatus = getEntitlementStatus(session.access_token);
        const rawAccessCode = session.user?.user_metadata?.accessCode;
        const accessCode = typeof rawAccessCode === 'string' && rawAccessCode.trim()
          ? rawAccessCode.trim()
          : null;
        const orgSlug = new URLSearchParams(search).get('org_slug');

        // If access code exists in metadata, this is an activation flow.
        // After activation completes, Vercel clears accessCode from metadata.
        // So returning users won't have it → login handoff.
        const isReturningUser = !accessCode && entitlementStatus === 'active';

        // ── Sleepio Canopy email magic link path ─────────────────────────────
        if (product === 'sleepio') {
          // Non-blocking: link anonymous PostHog session to the identified user.
          const email = session.user?.email;
          if (email) {
            getDistinctId({ variables: { email } })
              .then((result) => {
                const distinctId = result?.data?.posthogDistinctIdWithEmail?.distinctId;
                if (distinctId) {
                  posthog?.identify(distinctId, { email });
                }
              })
              .catch(() => {});
          }

          // Use the intent stored by EmailEntry — more reliable than inferring from
          // JWT entitlement status, which reads products[0] and can return 'active'
          // for a Spark entitlement even when the Sleepio entitlement is pending.
          // Fall back to entitlementStatus-based check if session storage is missing
          // (e.g. magic link opened in a different browser than the one that sent it).
          const storedIntent = sessionStorage.getItem(
            SLEEPIO_EMAIL_INTENT_SESSION_KEY,
          );
          sessionStorage.removeItem(SLEEPIO_EMAIL_INTENT_SESSION_KEY);
          const intent = storedIntent ?? (isReturningUser ? 'login' : 'sign-up');
          // Pass redirectTo through so ActivationRouter → CoverageForm → ActivationComplete
          // can build the native deep-link URL (sleep-companion://exchange-callback) that
          // dismisses the Canopy ASWebAuth session.
          const redirectQuery = redirectToParam && isSafeRedirectUri(redirectToParam)
            ? `?redirectTo=${encodeURIComponent(redirectToParam)}`
            : '';
          history.replace(
            orgSlug
              ? `/activate/sleepio-canopy/${lob}/${orgSlug}/${intent}${
                intent === 'login' ? '/complete' : ''
              }${redirectQuery}`
              : `/activate/${product}/access-code`,
          );
          return;
        }

        // ── Spark / access-code flow (original path) ─────────────────────────
        if (isReturningUser) {
          await handleLoginFlow();
          return;
        }

        // Confirm entitlement via the spark endpoint.
        // activateUserByAccessCode is called later in UserInfoConfirmation after demographics.
        try {
          await activateSparkConfirm({
            supabaseUserId,
            accessToken: session.access_token,
          });
        } catch (err) {
          logError('[EmailAuthCallback]', 'activation failed', err, {
            supabaseUserId,
          });
          history.replace(`/activate/${product}/access-code`, {
            error: 'email_verification_failed',
          });
          return;
        }

        // Employer flow (org slug but no access code): go to coverage check first.
        // Access code flow (has access code, may also have org slug from Cortex): skip coverage check.
        if (orgSlug && !accessCode) {
          history.replace(`/activate/${product}/coverage-check/${orgSlug}`, {
            patientUid,
            flow: 'email',
            accessToken: session.access_token,
          });
        } else {
          history.replace(`/activate/${product}/user-info/confirmation`, {
            patientUid,
            flow: 'email',
            accessCode,
          });
        }
      } catch (err) {
        logError('[EmailAuthCallback]', 'Unexpected error', err);
        history.replace(`/activate/${product}/access-code`, {
          error: 'email_verification_failed',
        });
      }
    };

    handleCallback();
  }, [history, product, lob, search, fetchMe, posthog, getDistinctId]); // eslint-disable-line

  return (
    <ThemeProvider theme={getTheme(product)}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress color="primary" />
      </div>
    </ThemeProvider>
  );
};

export default EmailAuthCallback;
